import './attachment-item.scss';

import { Skeleton, notification } from 'antd';
import { saveAs } from 'file-saver';
import React, { useEffect, useMemo, useState } from 'react';

// TODO: посмотреть дубликат функции base64toBlob и оставить одну
import { base64toBlob } from '@/components/App/Approvement/helpers';
import { DeleteOutlined, EyeOutlined } from '@/media/antd-icons';
import CloudDownloadIcon from '@/media/icons/cloud-download.svg?react';
import DownloadIcon from '@/media/icons/download.svg?react';
import { getFileExtension } from '@/services';
import { checkBase64 } from '@/services/helpers/strings';

import { FileIconPreview, ImagePreview } from './ui';

interface IProps {
  file: { base64: string; name: string };
  fixedWidth?: number;
  disableActions?: boolean;
  error?: boolean;
  hideName?: boolean;
  loaded?: boolean;
  loading?: boolean;
  minified?: boolean;
  onDelete?: () => void;
  onLoad?: () => Promise<boolean>;
}

const classNameBase = 'attachment-item';

const AttachmentItem: React.FC<IProps> = ({
  file,
  fixedWidth,
  disableActions = false,
  error = false,
  hideName = false,
  loaded = false,
  loading = false,
  minified = false,
  onDelete,
  onLoad
}) => {
  const [isImagePreviewError, setIsImagePreviewError] = useState(false);
  const [isLoadingError, setIsLoadingError] = useState(false);

  const { name, base64: base64Initial } = file;
  const base64 = base64Initial.replace(/^data:.*;base64,/, '');

  const { extension, isImage } = getFileExtension({ name });

  const isPdf = extension === 'pdf';
  const isPreviewable = (isImage || isPdf) && base64.length > 0;
  const computedDisableActions =
    disableActions || loading || (!loaded && !onLoad);

  const pdfObjectUrl = useMemo(() => {
    if (!isPdf) return '';
    return URL.createObjectURL(
      base64toBlob(`data:application/pdf;base64,${base64}`, 'application/pdf')
    );
  }, [base64]);

  const handleDownload = async () => {
    if (!base64) {
      notification.error({
        title: 'Не удалось загрузить файл. Попробуйте позже'
      });
      return;
    }
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const fileBlob = new Blob([byteArray], {
      type: `application/${extension}`
    });
    saveAs(fileBlob, name);
  };

  const handleClickPdf = () => {
    if (!isPdf) return;
    const pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.location.href = pdfObjectUrl;
      pdfWindow.document.title = name;
    }
  };

  const handlePreviewError = () => {
    setIsImagePreviewError(true);
  };

  const handleClick = async () => {
    if (computedDisableActions) return;

    if (!loaded) {
      if (onLoad) {
        const successLoad = await onLoad();
        setIsLoadingError(!successLoad);
      }
      return;
    }

    if (onDelete) {
      onDelete();
    } else if (!isPreviewable) {
      handleDownload();
    } else if (isPdf) {
      handleClickPdf();
    }
  };

  useEffect(() => {
    const { isValidBase64, isXML } = checkBase64(base64);
    if (!isValidBase64 || isXML) {
      setIsImagePreviewError(true);
    } else {
      setIsImagePreviewError(false);
    }
  }, [base64]);

  let styles: React.CSSProperties | undefined = undefined;
  if (fixedWidth !== undefined) {
    styles = styles || {};
    styles.width = `${fixedWidth}px`;
    styles.maxWidth = `${fixedWidth}px`;
    styles.height = `${fixedWidth}px`;
    styles.maxHeight = `${fixedWidth}px`;
    styles.minWidth = `${fixedWidth}px`;
    styles.minHeight = `${fixedWidth}px`;
  }

  const classNames: string[] = [classNameBase];
  const computedName = hideName ? '' : name;

  if (!computedDisableActions) {
    classNames.push(`${classNameBase}--actions-enabled`);
  }

  if (loading) {
    return (
      <div className={classNames.join(' ')} title={name} style={styles}>
        <Skeleton.Node
          active
          classNames={{
            content: `${classNameBase}__file-skeleton`
          }}
        />
      </div>
    );
  }
  if (!loaded) {
    return (
      <div
        className={classNames.join(' ')}
        onClick={handleClick}
        title={name}
        style={styles}
      >
        {!computedDisableActions && (
          <div className={`${classNameBase}__hover-action-wrapper`}>
            <CloudDownloadIcon
              className={`${classNameBase}__hover-action-icon`}
            />
          </div>
        )}
        <FileIconPreview
          extension={extension}
          fileName={computedName}
          isImage={isImage}
          minified={minified}
          halfOpacity
        />
        <div className={`${classNameBase}__corner-load-icon-container`}>
          <CloudDownloadIcon
            className={`${classNameBase}__not-loaded-icon ${isLoadingError ? `${classNameBase}__not-loaded-icon--error` : ''}`}
          />
        </div>
      </div>
    );
  }
  const hasError = error || isImagePreviewError;

  if (hasError) {
    classNames.push(`${classNameBase}--error`);
    if (!computedDisableActions && !!onDelete) {
      classNames.push(`${classNameBase}--clickable`);
    }
    return (
      <div
        className={classNames.join(' ')}
        title={name}
        style={styles}
        onClick={onDelete}
      >
        {!computedDisableActions && !!onDelete && (
          <div className={`${classNameBase}__hover-action-wrapper`}>
            <DeleteOutlined
              className={`${classNameBase}__hover-action-icon ${classNameBase}__hover-action-icon--delete`}
            />
          </div>
        )}
        <FileIconPreview
          extension={extension}
          fileName={computedName}
          isImage={isImage}
          showError={hasError}
          minified={minified}
        />
      </div>
    );
  }
  return (
    <div
      className={classNames.join(' ')}
      onClick={handleClick}
      title={name}
      style={styles}
    >
      {!computedDisableActions && !!onDelete && (
        <div className={`${classNameBase}__hover-action-wrapper`}>
          <DeleteOutlined
            className={`${classNameBase}__hover-action-icon ${classNameBase}__hover-action-icon--delete`}
          />
        </div>
      )}
      {isPreviewable && !isImagePreviewError ? (
        isImage ? (
          <ImagePreview
            base64={base64}
            extension={extension}
            fileName={computedName}
            onDownload={handleDownload}
            onError={handlePreviewError}
            disableActions={computedDisableActions}
          />
        ) : (
          <>
            {!computedDisableActions && !onDelete && (
              <div className={`${classNameBase}__hover-action-wrapper`}>
                <EyeOutlined
                  className={`${classNameBase}__hover-action-icon`}
                />
              </div>
            )}
            <FileIconPreview
              extension={extension}
              fileName={computedName}
              isImage={isImage}
              minified={minified}
            />
          </>
        )
      ) : (
        <>
          {!computedDisableActions && !onDelete && (
            <div className={`${classNameBase}__hover-action-wrapper`}>
              <DownloadIcon className={`${classNameBase}__hover-action-icon`} />
            </div>
          )}
          <FileIconPreview
            extension={extension}
            fileName={computedName}
            isImage={isImage}
            minified={minified}
          />
        </>
      )}
    </div>
  );
};

export default AttachmentItem;
